/**
 * Accessibility fixes for controls rendered and re-rendered by Nosto on PLPs.
 *
 * Nosto's facet checkboxes do not always respond to their native keyboard
 * activation. It also renders selected-filter chips such as "US Size: 5" as
 * <div href="..."> elements, which leaves them completely outside the tab
 * order. The theme cannot correct that remote markup at render time, so this
 * file progressively enhances each Nosto render and delegates activation.
 *
 * It also covers two related, silent-to-AT gaps in the same sidebar: the two
 * Nosto-owned navs (filter/sort, pagination) and facet checkboxes have no
 * accessible name, and facet groups (and Sort) are wrapped in role="menu"
 * around plain checkboxes/links, which is not a valid menu structure.
 *
 * NOT ported from the US reference: the result-count announcement machinery
 * (scheduleResultAnnouncement / markUserChangedFilters / resultCountMessage
 * and friends). Verified not to fire on live US - a Nosto PLP re-render never
 * lets the announcement debounce settle, so it never actually announces
 * anything in production. See PROJECT_PLAN.md §10-D1.
 */
(function () {
  'use strict';

  var FACET_CHECKBOX_SELECTOR = '[data-nosto-element="facet-setting"] input[type="checkbox"]';
  var ACTIVE_FILTER_SELECTOR = 'nav.ns-header-controls .active__filters__remove[data-filter-update-url]';
  var DISRUPTOR_SELECTOR = '.ns-disruptor.product-item--featured-image';
  var DISRUPTOR_LINK_SELECTOR = '.product-item__image-link[href]';
  var NOSTO_NAV_SELECTOR = 'nav.ns-header-controls, nav.pagination-custom';
  var SIDEBAR_MENU_SELECTOR = '.collection__sidebar__group [role="menu"]';
  var SIDEBAR_MENUITEM_SELECTOR = '.collection__sidebar__group [role="menuitem"]';
  var scheduledDisruptors = new WeakSet();

  function isActivationKey(e) {
    return e.key === 'Enter' || e.code === 'Enter' || e.key === ' ' || e.code === 'Space';
  }

  function enhanceActiveFilter(control) {
    control.setAttribute('tabindex', '0');
    control.setAttribute('role', 'button');

    var filterName = control.querySelector('small');
    var label = filterName ? filterName.textContent.trim() : control.textContent.trim();
    if (label) control.setAttribute('aria-label', 'Remove filter: ' + label);
  }

  function labelFacetCheckbox(checkbox) {
    if ((checkbox.getAttribute('aria-label') || '').trim()) return;
    var option = checkbox.parentElement.querySelector('label');
    var optionName = option && (option.textContent.trim() || option.querySelector('[data-swatch]')?.getAttribute('data-swatch'));
    var group = checkbox.closest('.collection__sidebar__group');
    var heading = group && group.querySelector('.collection__sidebar__heading span');
    var groupName = heading && heading.textContent.trim();
    if (optionName) checkbox.setAttribute('aria-label', (groupName ? groupName + ': ' : '') + optionName);
  }

  function labelNostoNavigation(nav) {
    if ((nav.getAttribute('aria-label') || '').trim()) return;
    nav.setAttribute('aria-label', nav.matches('.pagination-custom') ? 'Product pages' : 'Filter and sort products');
  }

  function relabelSidebarMenu(menu) {
    if (menu.getAttribute('role') !== 'menu') return;
    menu.setAttribute('role', 'group');
    var group = menu.closest('.collection__sidebar__group');
    var heading = group && group.querySelector('.collection__sidebar__heading span');
    var label = heading && heading.textContent.trim();
    if (label) menu.setAttribute('aria-label', label);
  }

  // Once the container is a group, Nosto's <li role="menuitem"> children are
  // orphaned (menuitem needs a menu owner). Removing the role outright isn't
  // enough either: the <li> would fall back to listitem inside a <ul> that is
  // no longer a list. role="none" drops the wrapper from the tree so each
  // facet checkbox sits directly in its named group. Handled separately from
  // relabelSidebarMenu so items Nosto re-renders into an already-relabelled
  // group are caught too.
  function neutraliseSidebarMenuItem(item) {
    item.setAttribute('role', 'none');
  }

  function labelFromHref(link) {
    try {
      var slug = new URL(link.href, window.location.href).pathname.split('/').filter(Boolean).pop();
      if (!slug) return 'Featured collection';
      var label = slug.replace(/-/g, ' ');
      return label.charAt(0).toUpperCase() + label.slice(1);
    } catch (error) {
      return 'Featured collection';
    }
  }

  function alignDisruptorWithVisualOrder(disruptor) {
    if (!disruptor.isConnected || !disruptor.parentElement) return;

    var grid = disruptor.parentElement;
    var tiles = Array.prototype.filter.call(grid.children, function (child) {
      return child.matches('.product-item') && child.getClientRects().length;
    });
    if (tiles.length < 2) return;

    var visualOrder = tiles.slice().sort(function (first, second) {
      var firstRect = first.getBoundingClientRect();
      var secondRect = second.getBoundingClientRect();
      return Math.abs(firstRect.top - secondRect.top) > 5
        ? firstRect.top - secondRect.top
        : firstRect.left - secondRect.left;
    });
    var visualIndex = visualOrder.indexOf(disruptor);
    if (visualIndex < 0) return;

    var nextVisualTile = visualOrder[visualIndex + 1];
    if (nextVisualTile && disruptor.nextElementSibling !== nextVisualTile) {
      grid.insertBefore(disruptor, nextVisualTile);
    } else if (!nextVisualTile && grid.lastElementChild !== disruptor) {
      grid.appendChild(disruptor);
    }
  }

  function enhanceDisruptor(disruptor) {
    var link = disruptor.querySelector(DISRUPTOR_LINK_SELECTOR);
    if (!link) return;

    link.setAttribute('tabindex', '0');
    if (!(link.getAttribute('aria-label') || '').trim()) {
      link.setAttribute('aria-label', labelFromHref(link));
    }

    if (scheduledDisruptors.has(disruptor)) return;
    scheduledDisruptors.add(disruptor);
    window.requestAnimationFrame(function () {
      alignDisruptorWithVisualOrder(disruptor);
      window.setTimeout(function () {
        alignDisruptorWithVisualOrder(disruptor);
        scheduledDisruptors.delete(disruptor);
      }, 250);
    });
  }

  function scan(root) {
    if (!root.querySelectorAll) return;
    if (root.nodeType === 1 && root.matches(ACTIVE_FILTER_SELECTOR)) enhanceActiveFilter(root);
    if (root.nodeType === 1 && root.matches(DISRUPTOR_SELECTOR)) enhanceDisruptor(root);
    if (root.nodeType === 1 && root.matches(NOSTO_NAV_SELECTOR)) labelNostoNavigation(root);
    if (root.nodeType === 1 && root.matches(FACET_CHECKBOX_SELECTOR)) labelFacetCheckbox(root);
    if (root.nodeType === 1 && root.matches(SIDEBAR_MENU_SELECTOR)) relabelSidebarMenu(root);
    if (root.nodeType === 1 && root.matches(SIDEBAR_MENUITEM_SELECTOR)) neutraliseSidebarMenuItem(root);
    root.querySelectorAll(ACTIVE_FILTER_SELECTOR).forEach(enhanceActiveFilter);
    root.querySelectorAll(DISRUPTOR_SELECTOR).forEach(enhanceDisruptor);
    root.querySelectorAll(NOSTO_NAV_SELECTOR).forEach(labelNostoNavigation);
    root.querySelectorAll(FACET_CHECKBOX_SELECTOR).forEach(labelFacetCheckbox);
    root.querySelectorAll(SIDEBAR_MENU_SELECTOR).forEach(relabelSidebarMenu);
    root.querySelectorAll(SIDEBAR_MENUITEM_SELECTOR).forEach(neutraliseSidebarMenuItem);
  }

  function onKeydown(e) {
    if (e.repeat) return;
    if (!isActivationKey(e)) return;

    const target = e.target;
    if (!target || !target.matches) return;

    if (target.matches(ACTIVE_FILTER_SELECTOR)) {
      var removeControl = target.querySelector('.filter__x');
      if (!removeControl) return;

      e.preventDefault();
      e.stopPropagation();
      // Match the pointer interaction exactly: Nosto listens for activation
      // on the chip's × control and removes only that individual filter.
      removeControl.click();
      return;
    }

    if (!target.matches(FACET_CHECKBOX_SELECTOR)) return;

    e.preventDefault();
    target.click();
  }

  scan(document);

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(scan);
    });
  }).observe(document.body, { childList: true, subtree: true });

  // Listen before Nosto's handlers so they cannot swallow keyboard input from
  // either the facet checkbox or the selected-filter removal chip.
  window.addEventListener('keydown', onKeydown, true);

  window.addEventListener('resize', function () {
    document.querySelectorAll(DISRUPTOR_SELECTOR).forEach(enhanceDisruptor);
  });
})();
